/**
 * Hook for checking integration OAuth status and triggering the OAuth flow.
 *
 * Used by ToolPicker to gate tool toggles behind OAuth connection.
 * Reuses the same OAuth popup + postMessage pattern from IntegrationsSettings.tsx.
 */

import type { IntegrationStatus, ServiceConfig } from "@/config/integrations";

import { useState, useCallback, useRef, useEffect } from "react";
import { useSessionStore } from "@webuild-ai/react-auth/stores";

import { getServiceForTool, toolRequiresOAuth, SERVICES } from "@/config/integrations";
import { useWebuildAPIClient } from "@/providers/WeBuildClientProvider";
import { siteConfig } from "@/config/site";
import { logger } from "@/telemetry";
import { isSafeUrl } from "@/utils/urlValidation";
import { useIntegrationStatusStore } from "@/stores/IntegrationStatusStore";

export interface UseIntegrationOAuthReturn {
  /** Check if tool is connected; if not, open OAuth popup. Resolves true/false. */
  ensureToolConnected: (toolId: string) => Promise<boolean>;
  /** The tool currently going through OAuth, or null */
  connectingToolId: string | null;
  /** Pre-fetch all integration statuses (call on mount to warm cache). Pass force=true to bypass cache TTL. Pass disabledTypes to skip disabled integrations. */
  prefetchStatuses: (force?: boolean, disabledTypes?: Set<string>) => Promise<void>;
  /** Get the cached connection status for a tool (for UI indicators) */
  getToolConnectionStatus: (toolId: string) => IntegrationStatus | undefined;
}

export function useIntegrationOAuth(): UseIntegrationOAuthReturn {
  const { authenticatedFetch } = useWebuildAPIClient();
  const { recordActivity } = useSessionStore();
  const { getStatus, setStatus, setStatuses, isCacheStale } = useIntegrationStatusStore();

  const [connectingToolId, setConnectingToolId] = useState<string | null>(null);
  const oauthCleanupRef = useRef<(() => void) | null>(null);

  const baseUrl = siteConfig().adminAPIURL.replace(/\/$/, "");

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (oauthCleanupRef.current) {
        oauthCleanupRef.current();
        oauthCleanupRef.current = null;
      }
    };
  }, []);

  /**
   * Fetch status for a single service, using cache if fresh.
   */
  const fetchServiceStatus = useCallback(
    async (service: ServiceConfig): Promise<IntegrationStatus> => {
      const cached = getStatus(service.id);

      if (cached && !isCacheStale()) {
        return cached;
      }

      if (!authenticatedFetch) {
        return { connected: false, integration_type: service.id };
      }

      try {
        const response = await authenticatedFetch(`${baseUrl}${service.endpoints.status}`);

        if (response.ok) {
          const data: IntegrationStatus = await response.json();

          setStatus(service.id, data);

          return data;
        }
      } catch (error) {
        logger.error(`Failed to fetch status for ${service.name}`, { error: String(error) });
      }

      const fallback: IntegrationStatus = {
        connected: false,
        integration_type: service.id,
      };

      setStatus(service.id, fallback);

      return fallback;
    },
    [authenticatedFetch, baseUrl, getStatus, isCacheStale, setStatus],
  );

  /**
   * Pre-fetch all integration statuses in parallel.
   */
  const prefetchStatuses = useCallback(
    async (force = false, disabledTypes?: Set<string>) => {
      if (!authenticatedFetch || (!force && !isCacheStale())) return;

      try {
        // Skip status fetch for globally disabled integrations
        const activeServices = disabledTypes
          ? SERVICES.filter((s) => !disabledTypes.has(s.id))
          : SERVICES;

        const results = await Promise.allSettled(
          activeServices.map(async (service) => {
            const response = await authenticatedFetch(`${baseUrl}${service.endpoints.status}`);

            if (response.ok) {
              const data: IntegrationStatus = await response.json();

              return { id: service.id, status: data };
            }

            return {
              id: service.id,
              status: { connected: false, integration_type: service.id } as IntegrationStatus,
            };
          }),
        );

        const statuses: Record<string, IntegrationStatus> = {};

        results.forEach((result) => {
          if (result.status === "fulfilled") {
            statuses[result.value.id] = result.value.status;
          }
        });

        setStatuses(statuses);
      } catch (error) {
        logger.error("Error prefetching integration statuses", { error: String(error) });
      }
    },
    [authenticatedFetch, baseUrl, isCacheStale, setStatuses],
  );

  /**
   * Open the OAuth popup for a service and return a promise
   * that resolves to true on success, false on failure/cancel.
   */
  const openOAuthPopup = useCallback(
    (service: ServiceConfig): Promise<boolean> => {
      return new Promise<boolean>((resolve) => {
        if (!authenticatedFetch) {
          resolve(false);

          return;
        }

        (async () => {
          try {
            recordActivity(false);
            const response = await authenticatedFetch(`${baseUrl}${service.endpoints.authorize}`);

            if (!response.ok) {
              resolve(false);

              return;
            }

            const data = await response.json();

            if (!isSafeUrl(data.auth_url)) {
              logger.error("OAuth auth_url failed safety check", { url: data.auth_url });
              resolve(false);

              return;
            }

            const width = 600;
            const height = 700;
            const left = window.screenX + (window.outerWidth - width) / 2;
            const top = window.screenY + (window.outerHeight - height) / 2;

            const popup = window.open(
              data.auth_url,
              `${service.id}-oauth`,
              `width=${width},height=${height},left=${left},top=${top},toolbar=no,menubar=no,scrollbars=yes`,
            );

            let isCleanedUp = false;
            let isResolved = false;

            const safeResolve = (value: boolean) => {
              if (isResolved) return;
              isResolved = true;
              resolve(value);
            };

            const cleanup = () => {
              if (isCleanedUp) return;
              isCleanedUp = true;
              window.removeEventListener("message", handleMessage);
              clearInterval(popupCheckInterval);
              setConnectingToolId(null);
            };

            // Poll to detect if popup was closed by the user (cancel)
            const popupCheckInterval = setInterval(() => {
              if (popup && popup.closed) {
                cleanup();
                safeResolve(false);
              }
            }, 500);

            const handleMessage = async (event: MessageEvent) => {
              const expectedOrigin = window.location.origin;

              if (event.origin !== expectedOrigin) return;
              if (event.source !== popup) return;

              if (
                event.data?.type === "google-oauth-callback" ||
                event.data?.type === "jira-oauth-callback" ||
                event.data?.type === "github-oauth-callback" ||
                event.data?.type === "oauth-callback"
              ) {
                const success = !!event.data.success;

                cleanup();

                if (success) {
                  recordActivity(false);
                  await new Promise((r) => setTimeout(r, 1500));
                  recordActivity(false);

                  // Refresh the status for this service
                  try {
                    const statusResponse = await authenticatedFetch(
                      `${baseUrl}${service.endpoints.status}`,
                    );

                    if (statusResponse.ok) {
                      const statusData: IntegrationStatus = await statusResponse.json();

                      setStatus(service.id, statusData);
                    }
                  } catch {
                    // Status refresh failed but OAuth succeeded
                  }
                }

                try {
                  popup?.close();
                } catch {
                  // Popup may already be closed
                }

                safeResolve(success);
              }
            };

            window.addEventListener("message", handleMessage);
            oauthCleanupRef.current = cleanup;
          } catch (error) {
            logger.error(`Failed to initiate OAuth for ${service.name}`, { error: String(error) });
            setConnectingToolId(null);
            resolve(false);
          }
        })();
      });
    },
    [authenticatedFetch, baseUrl, recordActivity, setStatus],
  );

  /**
   * Main entry point: ensure a tool's integration is connected.
   */
  const ensureToolConnected = useCallback(
    async (toolId: string): Promise<boolean> => {
      if (!toolRequiresOAuth(toolId)) {
        return true;
      }

      const service = getServiceForTool(toolId);

      if (!service) return true;

      const status = await fetchServiceStatus(service);

      if (
        status.connected &&
        status.token_status !== "expired" &&
        status.token_status !== "refresh_failed"
      ) {
        return true;
      }

      // Need OAuth — open popup
      setConnectingToolId(toolId);
      const success = await openOAuthPopup(service);

      setConnectingToolId(null);

      return success;
    },
    [fetchServiceStatus, openOAuthPopup],
  );

  /**
   * Get cached connection status for a tool (for UI indicator purposes).
   */
  const getToolConnectionStatus = useCallback(
    (toolId: string): IntegrationStatus | undefined => {
      if (!toolRequiresOAuth(toolId)) return undefined;
      const service = getServiceForTool(toolId);

      if (!service) return undefined;

      return getStatus(service.id);
    },
    [getStatus],
  );

  return {
    ensureToolConnected,
    connectingToolId,
    prefetchStatuses,
    getToolConnectionStatus,
  };
}
